import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAccount, useConnect, useDisconnect, useWalletClient } from "wagmi";
import { ethers } from "ethers";
import { Country, State, City } from "country-state-city";
import Select from "react-select";
import { formatEnglishDate } from "./utils/dateFormatting";
import LogoAppleTreeHD from "./LogoAppleTreeHD.svg";
import FileUploader from "./components/FileUploader";
import { esgApi, ApiError } from "./services/api";
import { ESG_QUESTIONS, QUESTIONS_BY_ID, getLegacyQuestionKeys } from "./shared/esgQuestions";
import LegalFooter from "./components/legal/LegalFooter";
import contractABI from "./contracts/AppleTree.json";
import {
  APTESG_ADDRESS,
  APTESG_POOL_ADDRESS,
  ARBITRUM_CHAIN_ID,
  ensureArbitrumNetwork,
  getArbitrumReadProvider,
  getFriendlyWalletError,
} from "./config/arbitrum";

// Importa il mapping centralizzato per retrocompatibilità con i fieldKey esistenti
const QUESTION_KEYS = getLegacyQuestionKeys();

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const ALLOWED_MIME_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/msword", "application/vnd.ms-excel"];
const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".docx", ".xlsx", ".doc", ".xls"];

// Indirizzi dei contratti
// Leggi l'indirizzo del contratto principale da .env per coerenza
const contractAddress = (
  (typeof import.meta !== "undefined" && import.meta.env && (import.meta.env.REACT_APP_APPLETREE_CONTRACT || import.meta.env.VITE_APPLETREE_CONTRACT))
  || (typeof process !== "undefined" && process.env && process.env.REACT_APP_APPLETREE_CONTRACT)
  || "0x58665Cf4742476532CA2387Bcc1631Af21C10c79"
);
const aptESGTokenAddress = APTESG_ADDRESS;
const poolAddress = APTESG_POOL_ADDRESS;

// Stili personalizzati per react-select
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    padding: "0 12px",
    borderRadius: "6px",
    border: "1px solid rgba(148,163,184,0.3)",
    background: "rgba(15,23,42,0.9)",
    color: "#f5f5f5",
    fontSize: "12px",
    boxSizing: "border-box",
    cursor: "pointer",
    minHeight: "40px",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(124,255,178,0.3)" : "none",
    borderColor: state.isFocused ? "rgba(124,255,178,0.5)" : "rgba(148,163,184,0.3)",
  }),
  input: (base) => ({
    ...base,
    color: "#f5f5f5",
    fontSize: "12px",
  }),
  placeholder: (base) => ({
    ...base,
    color: "rgba(248,113,113,0.5)",
    fontSize: "12px",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#f5f5f5",
    fontSize: "12px",
  }),
  menu: (base) => ({
    ...base,
    background: "rgba(15,23,42,0.95)",
    border: "1px solid rgba(124,255,178,0.3)",
    borderRadius: "6px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.5)",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "300px",
    background: "rgba(15,23,42,0.95)",
  }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected
      ? "rgba(124,255,178,0.2)"
      : state.isFocused
        ? "rgba(124,255,178,0.1)"
        : "transparent",
    color: state.isSelected ? "#7CFFB2" : "#f5f5f5",
    fontSize: "12px",
    padding: "8px 12px",
    cursor: "pointer",
    borderBottom: "1px solid rgba(148,163,184,0.1)",
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: "rgba(248,113,113,0.7)",
    fontSize: "12px",
  }),
};

// ABI ERC20 per il token APTESG
const erc20ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function transfer(address to, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)"
];

// ABI minimo per il pool Uniswap V3
const poolABI = [
  "function slot0() external view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint8 feeProtocol, bool unlocked)"
];

const getProvider = () => getArbitrumReadProvider();

// Componente per lo stato KYC
const KYCStatusSection = ({ address, isConnected }) => {
  const { data: walletClient } = useWalletClient();
  const { connector } = useAccount();
  const [kycStatus, setKycStatus] = useState(null); // null = loading, true = verified, false = not verified
  const [kycPaid, setKycPaid] = useState(false);
    const [kycFee, setKycFee] = useState(null); // null = loading, per mostrare stato di caricamento
  const [checking, setChecking] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (isConnected && address) {
      checkKYCStatus();
    }
  }, [isConnected, address]);

  const checkKYCStatus = async () => {
    setChecking(true);
    try {
      const provider = getProvider(walletClient);
      if (!provider) {
        console.warn("Provider not available");
        setChecking(false);
        return;
      }

      const contract = new ethers.Contract(contractAddress, contractABI, provider);
      
      // Verifica se l'utente è verificato on-chain
      const isVerified = await contract.verifiedInvestors(address);
      setKycStatus(isVerified);
      
      // Verifica se l'utente ha già pagato
      const hasPaid = await contract.kycPaid(address);
      setKycPaid(hasPaid);
      
      // Ottieni la fee del KYC
      const fee = await contract.kycFee();
      setKycFee(ethers.utils.formatEther(fee));
      
      console.log("✅ KYC Status:", { isVerified, hasPaid, fee: ethers.utils.formatEther(fee) });
    } catch (error) {
      console.error("❌ Error checking KYC status:", error);
      setKycStatus(false);
    } finally {
      setChecking(false);
    }
  };

  const handleStartKYC = async () => {
    if (!address) {
      alert("Please connect your wallet first");
      return;
    }

    setProcessing(true);
    try {
      const eip1193 = await ensureArbitrumNetwork(walletClient, connector);
      const provider = new ethers.providers.Web3Provider(eip1193, "any");
      const network = await provider.getNetwork();
      if (network.chainId !== ARBITRUM_CHAIN_ID) throw new Error("Wallet is not on Arbitrum One.");
      const signer = provider.getSigner();
      const contract = new ethers.Contract(contractAddress, contractABI, signer);

      // Se non ha ancora pagato, effettua il pagamento
      if (!kycPaid) {
        const feeToPay = ethers.utils.parseEther(kycFee.toString());
        
        console.log("📌 Starting KYC payment...");
        const tx = await contract.payForKYC({
          value: feeToPay,
          gasLimit: ethers.utils.hexlify(1000000),
        });

        console.log("✅ Transaction sent:", tx.hash);
        await tx.wait();
        alert("🎉 KYC Payment successful! Starting identity verification...");
      }

      // Avvia la sessione KYC con il backend
      const response = await fetch("https://aptesg-backend-aae2ea58fed0.herokuapp.com/start-kyc-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: address }),
      });

      const data = await response.json();
      if (data.success && data.verification_url) {
        window.open(data.verification_url, "_blank");
        alert("✅ KYC verification started! Please complete the verification in the new window.");
      } else {
        alert("❌ Failed to start KYC session. Please contact support.");
      }

      // Ricontrolla lo stato dopo qualche secondo
      setTimeout(() => {
        checkKYCStatus();
      }, 3000);
      
    } catch (error) {
      console.error("❌ Error starting KYC:", error);
      alert("❌ Error: " + getFriendlyWalletError(error));
    } finally {
      setProcessing(false);
    }
  };

  if (!isConnected) {
    return null;
  }

  return (
    <div style={{
      padding: "20px",
      background: "linear-gradient(135deg, rgba(34, 197, 94, 0.25) 0%, rgba(16, 185, 129, 0.2) 100%)",
      backdropFilter: "blur(10px)",
      borderRadius: "12px",
      border: "2px solid rgba(34, 197, 94, 0.4)",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.6)",
    }}>
      <h3 style={{ marginBottom: "16px", color: "#7CFFB2", fontSize: "16px", fontWeight: "700", textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>
        🔐 KYC Verification Status
      </h3>
      
      {checking ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <p style={{ color: "#E6FFE8", fontSize: "14px", textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}>Checking KYC status...</p>
        </div>
      ) : (
        <button
          onClick={kycStatus ? null : handleStartKYC}
          disabled={kycStatus || processing}
          style={{
            width: "100%",
            padding: "14px 20px",
            fontSize: "15px",
            fontWeight: "700",
            backgroundColor: kycStatus ? "#10b981" : processing ? "#6b7280" : "#22c55e",
            color: "white",
            border: "none",
            borderRadius: "10px",
            cursor: (kycStatus || processing) ? "not-allowed" : "pointer",
            transition: "all 0.3s ease",
            boxShadow: kycStatus ? "0 4px 12px rgba(16, 185, 129, 0.4)" : "0 4px 12px rgba(34, 197, 94, 0.4)",
            textShadow: "0 1px 3px rgba(0,0,0,0.5)",
            opacity: kycStatus ? 0.8 : 1,
          }}
          onMouseEnter={(e) => {
            if (!processing && !kycStatus) {
              e.target.style.backgroundColor = "#16a34a";
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(34, 197, 94, 0.5)";
            }
          }}
          onMouseLeave={(e) => {
            if (!processing && !kycStatus) {
              e.target.style.backgroundColor = "#22c55e";
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(34, 197, 94, 0.4)";
            }
          }}
        >
          {kycStatus 
            ? "✅ KYC Completed" 
            : processing 
              ? "Processing..." 
              : kycPaid 
                ? "🔄 Resume KYC Verification" 
                  : kycFee !== null ? `✨ Start KYC (${kycFee} ETH)` : "Loading..."
          }
        </button>
      )}
    </div>
  );
};

// Componente per il pagamento della Certification Fee
const CertificationFeeSection = ({ address, isConnected }) => {
  const { data: walletClient } = useWalletClient();
  const { connector } = useAccount();
  const [certificationFee, setCertificationFee] = useState(0);
  const [certificationFeeETH, setCertificationFeeETH] = useState(0);
  const [certificationFeeUSD, setCertificationFeeUSD] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("APTESG"); // "APTESG" o "ETH"
  const [hasPaid, setHasPaid] = useState(false);
  const [checking, setChecking] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentTxHash, setPaymentTxHash] = useState(null);
  const [aptESGPriceUSD, setAptESGPriceUSD] = useState(null);
  const [ethPriceUSD, setEthPriceUSD] = useState(null);

  useEffect(() => {
    if (isConnected && address) {
      checkCertificationFeeStatus();
      fetchPrices();
    }
  }, [isConnected, address]);

  // Ricalcola i valori quando i prezzi cambiano
  useEffect(() => {
    console.log("🔄 Recalculating fee with:", {
      certificationFee,
      aptESGPriceUSD,
      ethPriceUSD
    });
    if (certificationFee > 0 && aptESGPriceUSD && ethPriceUSD) {
      const feeUSD = parseFloat(certificationFee) * aptESGPriceUSD;
      const feeETH = feeUSD / ethPriceUSD;
      setCertificationFeeUSD(feeUSD);
      setCertificationFeeETH(feeETH);
      console.log("💰 Certification Fee calculated:", {
        APTESG: certificationFee,
        USD: feeUSD.toFixed(2),
        ETH: feeETH.toFixed(6)
      });
    } else {
      console.warn("⚠️ Cannot calculate - missing data:", {
        hasFee: certificationFee > 0,
        hasAPTESGPrice: !!aptESGPriceUSD,
        hasETHPrice: !!ethPriceUSD
      });
    }
  }, [certificationFee, aptESGPriceUSD, ethPriceUSD]);

  const fetchPrices = async () => {
    try {
      // Fetch sempre il prezzo ETH in USD da CoinGecko
      const ethResponse = await fetch("https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd");
      const ethData = await ethResponse.json();
      const ethPrice = ethData?.ethereum?.usd || 3000;
      setEthPriceUSD(ethPrice);
      console.log("✅ ETH price (CoinGecko):", ethPrice, "USD");

      // Metodo 1: Fetch APTESG price direttamente in USD da GeckoTerminal
      const response = await fetch(`https://api.geckoterminal.com/api/v2/networks/arbitrum/tokens/${aptESGTokenAddress.toLowerCase()}`);
      const data = await response.json();
      
      if (data.data && data.data.attributes && data.data.attributes.price_usd) {
        const price = parseFloat(data.data.attributes.price_usd);
        setAptESGPriceUSD(price);
        console.log("✅ APTESG price (GeckoTerminal):", price, "USD");
      } else {
        throw new Error("Price not found on GeckoTerminal");
      }
    } catch (error) {
      console.warn("⚠️ GeckoTerminal fallback, trying Uniswap pool...");
      
      // Metodo 2: Fallback al pool Uniswap V3
      try {
        const provider = getProvider(walletClient);
        if (provider) {
          const poolContract = new ethers.Contract(poolAddress, poolABI, provider);
          const slot0 = await poolContract.slot0();
          const sqrtPriceX96 = slot0[0];
          const tokenPriceInETH = (Number(sqrtPriceX96) / (2 ** 96)) ** 2;
          const aptESGPrice = tokenPriceInETH * (ethPriceUSD || 3000);
          setAptESGPriceUSD(aptESGPrice);
          console.log("✅ APTESG price (Uniswap Pool):", aptESGPrice.toFixed(4), "USD");
        }
      } catch (poolError) {
        console.error("❌ Error fetching prices:", poolError);
        setAptESGPriceUSD(0.63); // Ultimo fallback
      }
    }
  };

  const checkCertificationFeeStatus = async () => {
    setChecking(true);
    try {
      const provider = getProvider(walletClient);
      if (!provider) {
        console.warn("Provider not available");
        setChecking(false);
        return;
      }

      console.log("🔍 Reading from contract:", contractAddress);
      const contract = new ethers.Contract(contractAddress, contractABI, provider);
      
      // Ottieni la certification fee dal contratto (in APTESG)
      const fee = await contract.certificationFee();
      console.log("📋 Raw fee from contract:", fee.toString(), "wei");
      const feeInAPTESG = ethers.utils.formatEther(fee);
      console.log("📋 Certification Fee from contract:", feeInAPTESG, "APTESG");
      
      // Verifica il valore atteso
      if (feeInAPTESG !== "300.0" && feeInAPTESG !== "3000.0") {
        console.warn("⚠️ Unexpected fee value! Expected 300 or 3000, got:", feeInAPTESG);
      }
      
      setCertificationFee(feeInAPTESG);
      
      // Qui puoi verificare con il backend se l'utente ha già pagato
      // Per ora lo impostiamo a false, ma il backend dovrà tracciare i pagamenti
      // tramite eventi o verifiche dirette
    } catch (error) {
      console.error("❌ Error checking certification fee:", error);
      setCertificationFee("0");
    } finally {
      setChecking(false);
    }
  };

  const handlePayCertificationFee = async () => {
    if (!address) {
      alert("Please connect your wallet first");
      return;
    }

    setProcessing(true);
    try {
      const eip1193 = await ensureArbitrumNetwork(walletClient, connector);
      const provider = new ethers.providers.Web3Provider(eip1193, "any");
      const network = await provider.getNetwork();
      if (network.chainId !== ARBITRUM_CHAIN_ID) throw new Error("Wallet is not on Arbitrum One.");
      const signer = provider.getSigner();
      const contract = new ethers.Contract(contractAddress, contractABI, signer);

      // Ottieni l'indirizzo del treasury
      const treasuryAddress = await contract.treasury();
      
      if (treasuryAddress === "0x0000000000000000000000000000000000000000") {
        alert("⚠️ Treasury address not set. Please contact support.");
        setProcessing(false);
        return;
      }

      let tx;

      if (paymentMethod === "APTESG") {
        // PAGAMENTO IN APTESG
        const tokenContract = new ethers.Contract(aptESGTokenAddress, erc20ABI, signer);
        
        const decimals = await tokenContract.decimals();
        const feeAmount = ethers.utils.parseUnits(certificationFee.toString(), decimals);
        
        // Verifica il balance dell'utente
        const userBalance = await tokenContract.balanceOf(address);
        if (userBalance.lt(feeAmount)) {
          alert(`⚠️ Insufficient APTESG balance. You need ${certificationFee} APTESG.`);
          setProcessing(false);
          return;
        }
        
        console.log("📌 Sending Certification Fee in APTESG...");
        console.log("💰 Amount:", certificationFee, "APTESG");
        console.log("🏦 Treasury Address:", treasuryAddress);
        console.log("👤 From Address:", address);
        console.log("🪙 Token Address:", aptESGTokenAddress);

        tx = await tokenContract.transfer(treasuryAddress, feeAmount, {
          gasLimit: ethers.utils.hexlify(150000),
        });
      } else {
        // PAGAMENTO IN ETH
        const feeAmount = ethers.utils.parseEther(certificationFeeETH.toFixed(18));
        
        // Verifica il balance ETH dell'utente
        const userBalance = await provider.getBalance(address);
        if (userBalance.lt(feeAmount)) {
          alert(`⚠️ Insufficient ETH balance. You need ${certificationFeeETH.toFixed(6)} ETH.`);
          setProcessing(false);
          return;
        }
        
        console.log("📌 Sending Certification Fee in ETH...");
        console.log("💰 Amount:", certificationFeeETH.toFixed(6), "ETH");
        console.log("💵 USD Value:", certificationFeeUSD.toFixed(2));
        console.log("🏦 Treasury Address:", treasuryAddress);
        console.log("👤 From Address:", address);

        tx = await signer.sendTransaction({
          to: treasuryAddress,
          value: feeAmount,
          gasLimit: ethers.utils.hexlify(100000),
        });
      }

      console.log("✅ Transaction sent:", tx.hash);
      setPaymentTxHash(tx.hash);
      
      await tx.wait();
      
      const paymentDetails = paymentMethod === "APTESG" 
        ? `${certificationFee} APTESG (~$${certificationFeeUSD.toFixed(2)})`
        : `${certificationFeeETH.toFixed(6)} ETH (~$${certificationFeeUSD.toFixed(2)})`;

      alert(`🎉 Certification Fee paid successfully!\n\nTransaction: ${tx.hash}\n\nAmount: ${paymentDetails}\n\nThe backend will verify your payment.`);
      setHasPaid(true);
      
      // Notifica il backend del pagamento
      try {
        await fetch("https://aptesg-backend-aae2ea58fed0.herokuapp.com/certification-fee-paid", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            wallet: address,
            txHash: tx.hash,
            amount: paymentMethod === "APTESG" ? certificationFee : certificationFeeETH.toFixed(18),
            amountUSD: certificationFeeUSD.toFixed(2),
            tokenAddress: paymentMethod === "APTESG" ? aptESGTokenAddress : null,
            treasuryAddress: treasuryAddress,
            paymentType: paymentMethod
          }),
        });
      } catch (backendError) {
        console.warn("Backend notification failed (non-critical):", backendError);
      }
      
    } catch (error) {
      console.error("❌ Error paying certification fee:", error);
      alert("❌ Error: " + getFriendlyWalletError(error));
    } finally {
      setProcessing(false);
    }
  };

  if (!isConnected) {
    return null;
  }

  return (
    <div style={{
      padding: "20px",
      background: "linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(37, 99, 235, 0.2) 100%)",
      backdropFilter: "blur(10px)",
      borderRadius: "12px",
      border: "2px solid rgba(59, 130, 246, 0.4)",
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.6)",
    }}>
      <h3 style={{ marginBottom: "16px", color: "#93c5fd", fontSize: "16px", fontWeight: "700", textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>
        💳 Certification Fee
      </h3>
      
      {checking ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <p style={{ color: "#E6FFE8", fontSize: "14px", textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}>Loading fee...</p>
        </div>
      ) : (
        <>
          {/* Toggle per scegliere il metodo di pagamento */}
          {!hasPaid && (
            <div style={{ 
              marginBottom: "16px",
              display: "flex",
              gap: "10px",
              background: "rgba(15,23,42,0.6)",
              padding: "6px",
              borderRadius: "8px",
            }}>
              <button
                onClick={() => setPaymentMethod("APTESG")}
                disabled={processing}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  fontSize: "13px",
                  fontWeight: "600",
                  background: paymentMethod === "APTESG" ? "rgba(59, 130, 246, 0.8)" : "transparent",
                  color: paymentMethod === "APTESG" ? "white" : "#93c5fd",
                  border: "none",
                  borderRadius: "6px",
                  cursor: processing ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                }}
              >
                🪙 APTESG
              </button>
              <button
                onClick={() => setPaymentMethod("ETH")}
                disabled={processing}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  fontSize: "13px",
                  fontWeight: "600",
                  background: paymentMethod === "ETH" ? "rgba(59, 130, 246, 0.8)" : "transparent",
                  color: paymentMethod === "ETH" ? "white" : "#93c5fd",
                  border: "none",
                  borderRadius: "6px",
                  cursor: processing ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                }}
              >
                💎 ETH
              </button>
            </div>
          )}

          {/* Mostra l'importo in base al metodo selezionato */}
          {!hasPaid && certificationFee > 0 && (
            <div style={{
              marginBottom: "12px",
              padding: "12px",
              background: "rgba(96, 165, 250, 0.25)",
              borderRadius: "8px",
              textAlign: "center",
              border: "1px solid rgba(59, 130, 246, 0.4)",
            }}>
              {paymentMethod === "APTESG" ? (
                <>
                  <p style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: "#E6FFE8", textShadow: "0 2px 4px rgba(0,0,0,0.9)" }}>
                    🪙 {certificationFee} APTESG
                  </p>
                  {certificationFeeUSD > 0 && (
                    <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#1e3a8a", textShadow: "0 1px 2px rgba(255,255,255,0.3)" }}>
                      ≈ ${certificationFeeUSD.toFixed(2)} USD
                    </p>
                  )}
                </>
              ) : (
                <>
                  {certificationFeeETH > 0 ? (
                    <>
                      <p style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: "#E6FFE8", textShadow: "0 2px 4px rgba(0,0,0,0.9)" }}>
                        💎 {certificationFeeETH.toFixed(6)} ETH
                      </p>
                      {certificationFeeUSD > 0 && (
                        <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#1e3a8a", textShadow: "0 1px 2px rgba(255,255,255,0.3)" }}>
                          ≈ ${certificationFeeUSD.toFixed(2)} USD
                        </p>
                      )}
                    </>
                  ) : (
                    <p style={{ margin: 0, fontSize: "13px", color: "#E6FFE8", textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}>
                      Calculating ETH price...
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <button
            onClick={hasPaid ? null : handlePayCertificationFee}
            disabled={hasPaid || processing}
            style={{
              width: "100%",
              padding: "14px 20px",
              fontSize: "15px",
              fontWeight: "700",
              backgroundColor: hasPaid ? "#3b82f6" : processing ? "#6b7280" : "#2563eb",
              color: "white",
              border: "none",
              borderRadius: "10px",
              cursor: (hasPaid || processing) ? "not-allowed" : "pointer",
              transition: "all 0.3s ease",
              boxShadow: hasPaid ? "0 4px 12px rgba(59, 130, 246, 0.4)" : "0 4px 12px rgba(37, 99, 235, 0.4)",
              textShadow: "0 1px 3px rgba(0,0,0,0.5)",
              opacity: hasPaid ? 0.8 : 1,
            }}
            onMouseEnter={(e) => {
              if (!processing && !hasPaid) {
                e.target.style.backgroundColor = "#1d4ed8";
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 16px rgba(37, 99, 235, 0.5)";
              }
            }}
            onMouseLeave={(e) => {
              if (!processing && !hasPaid) {
                e.target.style.backgroundColor = "#2563eb";
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 12px rgba(37, 99, 235, 0.4)";
              }
            }}
          >
            {hasPaid 
              ? "✅ Fee Paid" 
              : processing 
                ? "Processing..." 
                : paymentMethod === "APTESG"
                  ? `💰 Pay with APTESG`
                  : `💰 Pay with ETH`
            }
          </button>
        </>
      )}
      
      {paymentTxHash && (
        <p style={{ 
          marginTop: "10px", 
          fontSize: "11px", 
          color: "#93c5fd",
          textAlign: "center",
          textShadow: "0 1px 3px rgba(0,0,0,0.9)",
          wordBreak: "break-all"
        }}>
          TX: {paymentTxHash.slice(0, 10)}...{paymentTxHash.slice(-8)}
        </p>
      )}
    </div>
  );
};

// Componente per la sezione del wallet
const WalletSection = () => {
  const { address, isConnected } = useAccount();
  const { connectAsync, connectors } = useConnect();
  const { disconnect } = useDisconnect();

  const connectWallet = async () => {
    try {
      if (isConnected) return;

      const injectedConnector = connectors.find((item) => item.id === "injected");
      const walletConnectConnector = connectors.find((item) => item.id === "walletConnect");
      const selectedConnector = window.ethereum && injectedConnector
        ? injectedConnector
        : walletConnectConnector;

      if (!selectedConnector) throw new Error("No compatible wallet connector is available.");
      await connectAsync({ connector: selectedConnector, chainId: ARBITRUM_CHAIN_ID });
    } catch (error) {
      console.error("Connection error:", error);
    }
  };

  return (
    <div style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 12 }}>
      {isConnected && address ? (
        <>
          <span style={{ 
            color: "#fff", 
            fontWeight: 600, 
            backgroundColor: "rgba(59, 130, 246, 0.25)",
            backdropFilter: "blur(8px)",
            padding: "8px 14px",
            borderRadius: "8px",
            border: "1px solid rgba(59, 130, 246, 0.4)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.4)",
            textShadow: "0 1px 3px rgba(0,0,0,0.8)"
          }}>
            🔗 {address.slice(0, 6)}...{address.slice(-4)}
          </span>
          <button
            onClick={() => disconnect()}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#c0392b";
              e.target.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#e74c3c";
              e.target.style.transform = "scale(1)";
            }}
            style={{
              padding: "6px 12px",
              fontSize: 12,
              backgroundColor: "#e74c3c",
              color: "white",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              fontWeight: 500,
              transition: "all 0.2s ease",
            }}
          >
            Disconnect Wallet
          </button>
        </>
      ) : (
        <button
          onClick={connectWallet}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#15893d";
            e.target.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#18A34A";
            e.target.style.transform = "scale(1)";
          }}
          style={{
            padding: "6px 12px",
            fontSize: 12,
            backgroundColor: "#18A34A",
            color: "white",
            border: "none",
            borderRadius: 4,
            cursor: "pointer",
            fontWeight: 500,
            transition: "all 0.2s ease",
          }}
        >
          Connect Wallet
        </button>
      )}
    </div>
  );
};

const CompanyVerification = () => {
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const [expandedSection, setExpandedSection] = useState(null);
  const [uploadStates, setUploadStates] = useState({});
  const [uploadedAttachments, setUploadedAttachments] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [validationState, setValidationState] = useState({ missingQuestionKeys: [] });
  const [submittedRequest, setSubmittedRequest] = useState(null); // { requestId, status, createdAt }

  const missingQuestionKeys = useMemo(
    () => validationState?.missingQuestionKeys || [],
    [validationState]
  );

  const missingBySection = useMemo(() => {
    return missingQuestionKeys.reduce((acc, questionKey) => {
      const section = QUESTIONS_BY_ID.get(questionKey)?.section;
      if (section) acc[section] = (acc[section] || 0) + 1;
      return acc;
    }, {});
  }, [missingQuestionKeys]);

  const getMissingCountForSection = (section) => missingBySection[section] || 0;

  const clearQuestionValidation = (questionKey) => {
    if (!questionKey) return;
    setValidationState((prev) => {
      const current = prev?.missingQuestionKeys || [];
      if (!current.includes(questionKey)) return prev;
      return {
        ...prev,
        missingQuestionKeys: current.filter((key) => key !== questionKey),
      };
    });
  };
  
  // Form disabilitato solo se pending o approved, riabilitato se rejected o need_info
  const isFormDisabled = submittedRequest && (submittedRequest.status === 'pending' || submittedRequest.status === 'approved');
  
  // Funzione per pulire i dati ESG dal localStorage (usata quando 404)
  const cleanupESGData = () => {
    const keysToRemove = ['esgSubmittedRequest', 'esgRequestId', 'currentRequestId', 'companyVerificationId'];
    keysToRemove.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`🗑️ Rimosso dal localStorage: ${key}`);
      }
    });
  };
  
  // Carica richiesta salvata dal localStorage
  React.useEffect(() => {
    if (address && isConnected) {
      const savedRequest = localStorage.getItem('esgSubmittedRequest');
      if (savedRequest) {
        try {
          const requestData = JSON.parse(savedRequest);
          
          // Validazione aggiuntiva: verifica che l'ID sia un UUID valido
          if (!requestData.requestId || requestData.requestId.trim() === '') {
            console.warn('⚠️ Richiesta salvata non valida: ID vuoto');
            cleanupESGData();
            return;
          }
          
          setSubmittedRequest(requestData);
        } catch (error) {
          console.error("Error loading saved request:", error);
          cleanupESGData();
        }
      }
    }
  }, [address, isConnected]);
  
  // Polling per aggiornare lo status della richiesta dal backend
  React.useEffect(() => {
    if (submittedRequest && submittedRequest.requestId) {
      const checkStatus = async () => {
        try {
          // Prima valida che l'ID esista
          const isValid = await esgApi.validateRequestId(submittedRequest.requestId);
          
          if (!isValid) {
            // L'ID non è valido - la richiesta non esiste più nel backend
            console.error('❌ Richiesta non trovata - ID non valido, pulizia in corso...');
            
            // Pulisci localStorage e state
            cleanupESGData();
            
            // Mostra errore e resetta UI
            setSubmitError('⚠️ Request not found: The previous request is no longer available or the ID has expired. Please create a new request.');
            setSubmittedRequest(null);
            
            return;
          }
          
          // Se valido, ottieni lo stato aggiornato
          const response = await esgApi.getRequestStatus(submittedRequest.requestId);
          if (response.status !== submittedRequest.status) {
            const updatedRequest = { ...submittedRequest, status: response.status };
            setSubmittedRequest(updatedRequest);
            localStorage.setItem('esgSubmittedRequest', JSON.stringify(updatedRequest));
            console.log('📝 Status aggiornato:', response.status);
          }
        } catch (error) {
          if (error instanceof ApiError && error.status === 404) {
            // 404: ID richiesta non valido
            console.error('❌ Errore 404 - Richiesta non trovata');
            cleanupESGData();
            setSubmitError('⚠️ Request not found: The request ID is invalid or has expired.');
            setSubmittedRequest(null);
          } else {
            console.error("Error checking status:", error.message || error);
          }
        }
      };
      
      // Check immediato
      checkStatus();
      
      // Polling ogni 10 secondi se pending o need_info
      if (submittedRequest.status === 'pending' || submittedRequest.status === 'need_info') {
        const interval = setInterval(checkStatus, 10000);
        return () => clearInterval(interval);
      }
    }
  }, [submittedRequest]);
  
  const [formData, setFormData] = useState({
    // Company Info
    wallet: address || "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    companyName: "",
    registrationNumber: "",
    addressCountry: "",
    addressState: "",
    addressCity: "",
    addressLine: "",
    position: "",
    businessSector: "",
    businessSectorOther: "",
    numberOfEmployees: "",
    companyWebsite: "",
    companyLogo: null,
    
    // E - ENVIRONMENT (40 points)
    e1_1_ghgScope12: "", e1_1_description: "", e1_1_file: null, e1_1_notes: "",
    e1_2_scope3: "", e1_2_description: "", e1_2_file: null, e1_2_notes: "",
    e1_3_reductionTargets: "", e1_3_description: "", e1_3_file: null, e1_3_notes: "",
    e1_4_renewableEnergy: "", e1_4_percentage: "", e1_4_file: null, e1_4_notes: "",
    e2_1_wastePolicy: "", e2_1_description: "", e2_1_file: null, e2_1_notes: "",
    e2_2_recyclingRate: "", e2_2_description: "", e2_2_file: null, e2_2_notes: "",
    e2_3_packagingReduction: "", e2_3_description: "", e2_3_file: null, e2_3_notes: "",
    e3_1_supplierCriteria: "", e3_1_description: "", e3_1_file: null, e3_1_notes: "",
    e3_2_supplierMonitoring: "", e3_2_description: "", e3_2_file: null, e3_2_notes: "",
    e4_1_waterMeasurement: "", e4_1_description: "", e4_1_file: null, e4_1_notes: "",
    e4_2_wastewater: "", e4_2_description: "", e4_2_file: null, e4_2_notes: "",
    e5_1_greenProducts: "", e5_1_description: "", e5_1_file: null, e5_1_notes: "",
    e5_2_certifications: "", e5_2_description: "", e5_2_file: null, e5_2_notes: "",
    
    // S - SOCIAL (35 points)
    s1_1_regularContracts: "", s1_1_description: "", s1_1_file: null, s1_1_notes: "",
    s1_2_healthSafety: "", s1_2_description: "", s1_2_file: null, s1_2_notes: "",
    s1_3_training: "", s1_3_description: "", s1_3_file: null, s1_3_notes: "",
    s2_1_antiDiscrimination: "", s2_1_description: "", s2_1_file: null, s2_1_notes: "",
    s2_2_diversity: "", s2_2_description: "", s2_2_file: null, s2_2_notes: "",
    s3_1_childLaborPolicy: "", s3_1_description: "", s3_1_file: null, s3_1_notes: "",
    s3_2_supplyChainRights: "", s3_2_description: "", s3_2_file: null, s3_2_notes: "",
    s4_1_communityRelations: "", s4_1_description: "", s4_1_file: null, s4_1_notes: "",
    s4_2_stakeholderEngagement: "", s4_2_description: "", s4_2_file: null, s4_2_notes: "",
    s5_1_gdpr: "", s5_1_description: "", s5_1_file: null, s5_1_notes: "",
    s5_2_cybersecurity: "", s5_2_description: "", s5_2_file: null, s5_2_notes: "",
    
    // G - GOVERNANCE (25 points)
    g1_1_ethicsCode: "", g1_1_description: "", g1_1_file: null, g1_1_notes: "",
    g1_2_antiCorruption: "", g1_2_description: "", g1_2_file: null, g1_2_notes: "",
    g1_3_whistleblowing: "", g1_3_description: "", g1_3_file: null, g1_3_notes: "",
    g2_1_ownershipStructure: "", g2_1_description: "", g2_1_file: null, g2_1_notes: "",
    g2_2_governance: "", g2_2_description: "", g2_2_file: null, g2_2_notes: "",
    g2_3_boardDiversity: "", g2_3_description: "", g2_3_file: null, g2_3_notes: "",
    g3_1_regulatoryMapping: "", g3_1_description: "", g3_1_file: null, g3_1_notes: "",
    g3_2_regulatoryUpdates: "", g3_2_description: "", g3_2_file: null, g3_2_notes: "",
    g4_1_financialStatements: "", g4_1_description: "", g4_1_file: null, g4_1_notes: "",
    g4_2_esgReporting: "", g4_2_description: "", g4_2_file: null, g4_2_notes: "",
    g5_1_riskMapping: "", g5_1_description: "", g5_1_file: null, g5_1_notes: "",
    g5_2_incidentResponse: "", g5_2_description: "", g5_2_file: null, g5_2_notes: "",
    
    // Transparency & Privacy Consents
    privacyConsent: false,
    mapVisibility: "CITY_REGION" // Default: CITY_REGION
  });

  const countries = useMemo(() => Country.getAllCountries(), []);
  const states = useMemo(() => {
    if (!formData.addressCountry) return [];
    return State.getStatesOfCountry(formData.addressCountry);
  }, [formData.addressCountry]);
  const cities = useMemo(() => {
    if (!formData.addressCountry) return [];
    if (formData.addressState) {
      return City.getCitiesOfState(formData.addressCountry, formData.addressState);
    }
    return City.getCitiesOfCountry(formData.addressCountry);
  }, [formData.addressCountry, formData.addressState]);

  // TEMPORARILY DISABLED FOR TESTING - uncomment after viewing
  // if (!isConnected) {
  //   return (
  //     <div style={{ minHeight: "100vh", background: "#050816", color: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
  //       <div style={{ textAlign: "center" }}>
  //         <h2>Please connect your wallet to proceed</h2>
  //         <button 
  //           onClick={() => navigate("/")}
  //           style={{ marginTop: "20px", padding: "10px 20px", background: "#22c55e", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "16px" }}
  //         >
  //           Go Back
  //         </button>
  //       </div>
  //     </div>
  //   );
  // }

  const toggleSection = (section) => {
    const newExpandedSection = expandedSection === section ? null : section;
    setExpandedSection(newExpandedSection);
    
    // Scroll automatico all'inizio della card quando viene aperta
    if (newExpandedSection) {
      setTimeout(() => {
        const element = document.getElementById(`section-${section}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const validateFile = (file) => {
    if (!file) return { valid: false, error: "No file selected" };
    if (file.size > MAX_FILE_SIZE) return { valid: false, error: `File too large (max 20MB)` };
    
    // Verifica mime type OPPURE estensione (fallback per Android)
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const isMimeValid = ALLOWED_MIME_TYPES.includes(file.type);
    const isExtensionValid = ALLOWED_EXTENSIONS.includes(fileExtension);
    
    if (!isMimeValid && !isExtensionValid) {
      return { valid: false, error: `Invalid file type (allowed: ${ALLOWED_EXTENSIONS.join(', ')})` };
    }
    
    return { valid: true };
  };

  const handleFileUpload = async (fieldKey, file) => {
    const questionKey = QUESTION_KEYS[fieldKey];
    if (!questionKey) {
      setUploadStates(prev => ({ ...prev, [fieldKey]: { status: 'error', progress: 0, error: 'Invalid question mapping. Please refresh and retry.' }}));
      return;
    }
    
    const validation = validateFile(file);
    if (!validation.valid) {
      setUploadStates(prev => ({ ...prev, [fieldKey]: { status: 'error', progress: 0, error: validation.error }}));
      return;
    }
    
    setUploadStates(prev => ({ ...prev, [fieldKey]: { status: 'uploading', progress: 0, error: '' }}));
    
    try {
      const { uploadUrl, storageKey } = await esgApi.getPresignedUploadUrl({
        fileName: file.name,
        contentType: file.type,
        size: file.size,
        questionKey
      });
      
      await esgApi.uploadFileToR2(uploadUrl, file, (progress) => {
        setUploadStates(prev => ({ ...prev, [fieldKey]: { ...prev[fieldKey], progress }}));
      });
      
      setUploadedAttachments(prev => ({ ...prev, [questionKey]: {
        storageKey, fileName: file.name, size: file.size, contentType: file.type
      }}));
      
      setUploadStates(prev => ({ ...prev, [fieldKey]: { status: 'success', progress: 100, error: '' }}));
    } catch (error) {
      console.error('Upload error:', error);
      let errorMsg = error.message;
      if (errorMsg && errorMsg.includes('Endpoint ESG non trovato')) {
        errorMsg = '⚠️ Backend non pronto. Verifica BACKEND_ESG_ENDPOINTS_REQUIRED.md';
      }
      setUploadStates(prev => ({ ...prev, [fieldKey]: { status: 'error', progress: 0, error: errorMsg }}));
    }
  };

  const handleFileChange = (fieldKey, file) => {
    setFormData({ ...formData, [`${fieldKey}_file`]: file });
    if (file) handleFileUpload(fieldKey, file);
  };

  const handleSubmit = async () => {
    setSubmitError("");
    setValidationState({ missingQuestionKeys: [] });
    setSubmitting(true);
    
    try {
      // Validazione campi obbligatori della Company Info
      const requiredFields = {
        firstName: "First Name",
        lastName: "Last Name",
        email: "Email Address",
        phoneNumber: "Phone Number",
        companyName: "Company Name",
        addressCountry: "Country",
        addressCity: "City",
        addressLine: "Address Line",
        position: "Position",
        businessSector: "Business Sector",
        numberOfEmployees: "Number of Employees"
      };
      
      const missingFields = [];
      for (const [field, label] of Object.entries(requiredFields)) {
        if (!formData[field] || formData[field].trim() === "") {
          missingFields.push(label);
        }
      }
      
      // Validazione campo condizionale businessSectorOther
      if (formData.businessSector === "other" && (!formData.businessSectorOther || formData.businessSectorOther.trim() === "")) {
        missingFields.push("Business Sector (please specify)");
      }

      const statesForCountry = formData.addressCountry ? State.getStatesOfCountry(formData.addressCountry) : [];
      if (statesForCountry.length > 0 && (!formData.addressState || formData.addressState.trim() === "")) {
        missingFields.push("State/Region");
      }

      // Validazione risposte ESG: backend si aspetta chiavi q06-q41 (36 domande)
      const missingEsgQuestionKeys = Object.entries(QUESTION_KEYS)
        .filter(([fieldKey]) => {
          const value = formData[fieldKey];
          return value === undefined || value === null || String(value).trim() === "";
        })
        .map(([_, questionKey]) => questionKey);

      if (missingEsgQuestionKeys.length > 0) {
        setValidationState({ missingQuestionKeys: missingEsgQuestionKeys });

        const firstMissingSection = QUESTIONS_BY_ID.get(missingEsgQuestionKeys[0])?.section;
        if (firstMissingSection === "E") setExpandedSection("environment");
        if (firstMissingSection === "S") setExpandedSection("social");
        if (firstMissingSection === "G") setExpandedSection("governance");

        const missingEsgQuestions = missingEsgQuestionKeys
        .map((questionKey) => {
          const questionMeta = QUESTIONS_BY_ID.get(questionKey);
          if (!questionMeta) return questionKey;
          return `${questionMeta.visualNumber} (${questionKey})`;
        });

        const preview = missingEsgQuestions.slice(0, 6).join(", ");
        const remaining = missingEsgQuestions.length - 6;
        throw new Error(
          `Missing ESG answers (${missingEsgQuestions.length}/36): ${preview}${remaining > 0 ? ` ... +${remaining}` : ""}`
        );
      }
      
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
      }
      
      const pendingUploads = Object.values(uploadStates).some(s => s.status === 'uploading');
      if (pendingUploads) throw new Error("Wait for uploads to complete");
      
      // Prepara answers come array (formato richiesto dal backend)
      const answersArray = [];
      Object.entries(QUESTION_KEYS).forEach(([fieldKey, questionKey]) => {
        const value = formData[fieldKey];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          const answerObj = {
            questionKey,
            answer: value
          };
          
          // Aggiungi la description/evidence se presente
          const descriptionKey = `${fieldKey}_description`;
          if (formData[descriptionKey]) {
            answerObj.description = formData[descriptionKey];
          }
          
          answersArray.push(answerObj);
        }
      });
      
      // Prepara attachments come array (formato richiesto dal backend)
      const attachmentsArray = [];
      Object.entries(uploadedAttachments).forEach(([questionKey, attachment]) => {
        attachmentsArray.push({
          questionKey,
          fileName: attachment.fileName,
          storageKey: attachment.storageKey,
          size: attachment.size,
          contentType: attachment.contentType || attachment.mimeType,
          mimeType: attachment.mimeType || attachment.contentType
        });
      });
      
      const selectedCountry = formData.addressCountry ? Country.getCountryByCode(formData.addressCountry) : null;
      const selectedState = (formData.addressCountry && formData.addressState)
        ? State.getStateByCodeAndCountry(formData.addressState, formData.addressCountry)
        : null;
      const fullAddress = [
        formData.addressLine,
        formData.addressCity,
        selectedState?.name,
        selectedCountry?.name
      ].filter(Boolean).join(", ");

      // Payload nel formato richiesto dal backend
      const payload = {
        companyName: formData.companyName,
        vatNumber: formData.registrationNumber,
        email: formData.email,
        phone: formData.phoneNumber,
        address: fullAddress,
        walletAddress: formData.wallet || address || "", // Wallet address dal form o dalla connessione
        answers: answersArray,
        attachments: attachmentsArray,
        // Privacy & Transparency Consents
        privacyConsent: formData.privacyConsent, // Always true when submitting (blocked by validation)
        mapVisibility: formData.mapVisibility, // EXACT_ADDRESS | CITY_REGION | COUNTRY_ONLY | HIDDEN
        consentVersion: "v1" // Consent version for tracking
      };
      
      console.log("📤 Submitting payload:", payload);
      console.log("📊 Answers count:", answersArray.length);
      console.log("📎 Attachments count:", attachmentsArray.length);
      
      const response = await esgApi.submitRequest(payload);
      
      console.log("✅ Backend response:", response);
      
      // Verifica se la risposta è effettivamente un successo
      if (!response || response.ok === false) {
        // Il backend ha risposto con ok: false o non ha risposto correttamente
        const requestId = response?.id || response?.requestId || response?._id || "N/A";
        throw new Error(`Error sending email. RequestId: ${requestId}`);
      }
      
      // Estrai requestId dalla risposta
      const requestId = response.id || response.requestId || response._id;
      
      if (!requestId) {
        throw new Error("Invalid backend response: missing requestId");
      }
      
      // Salva lo stato della richiesta inviata
      const submissionData = {
        requestId: requestId,
        status: response.status || 'pending',
        createdAt: response.createdAt || new Date().toISOString(),
        email: formData.email,
        companyName: formData.companyName
      };
      
      setSubmittedRequest(submissionData);
      setValidationState({ missingQuestionKeys: [] });
      
      // Salva in localStorage per persistenza
      localStorage.setItem('esgSubmittedRequest', JSON.stringify(submissionData));
      
      console.log("✅ Richiesta salvata con successo:", submissionData);
    } catch (error) {
      console.error("Submit error:", error);
      let errorMsg = error.message || "Submission failed";
      
      // Migliora i messaggi di errore
      if (error instanceof ApiError) {
        if (error.status === 404) {
          // 404 durante il submit: problemi con l'endpoint o backend
          errorMsg = "❌ Request not found: The backend may not have saved the request. Please try again.";
        } else if (error.status >= 500) {
          // 500+: Problemi server
          errorMsg = "❌ Backend error: The server encountered an error. Please try again later.";
        } else if (error.status === 400) {
          // 400: Dati non validi
          const rawDetails = error?.data?.details;
          let detailText = Array.isArray(rawDetails)
            ? rawDetails
                .map((item) => item?.message || item?.field || item?.path)
                .filter(Boolean)
                .join(" • ")
            : (typeof rawDetails === "string" ? rawDetails : "");

          // Arricchisce eventuali chiavi q.. con numero visuale e titolo domanda
          detailText = detailText.replace(/q\d{2}_[a-z0-9_]+/gi, (match) => {
            const questionMeta = QUESTIONS_BY_ID.get(match);
            if (!questionMeta) return match;
            return `${questionMeta.visualNumber} ${questionMeta.title} [${match}]`;
          });

          errorMsg = detailText
            ? `❌ Invalid data: ${detailText}`
            : "❌ Invalid data: Please make sure all fields are filled in correctly.";
        }
      } else if (errorMsg && errorMsg.includes('Endpoint ESG non trovato')) {
        // Messaggio legacy - pulisci e correggi
        errorMsg = "❌ Backend unavailable: ESG endpoints may not be implemented yet.";
      }
      
      setSubmitError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const getUploadStatusIndicator = (fieldKey) => {
    const state = uploadStates[fieldKey];
    if (!state || state.status === 'idle') return null;
    if (state.status === 'uploading') return <div style={{fontSize:"11px",color:"#3b82f6",marginTop:"4px"}}>⏳ Uploading... {state.progress}%</div>;
    if (state.status === 'success') return <div style={{fontSize:"11px",color:"#22c55e",marginTop:"4px"}}>✓ Complete</div>;
    if (state.status === 'error') return <div style={{fontSize:"11px",color:"#ef4444",marginTop:"4px"}}>✗ {state.error}</div>;
  };

  const renderQuestion = (id, question, fieldKey, options = null, showPercentage = false) => {
    const answer = formData[fieldKey];
    const description = formData[`${fieldKey}_description`];
    const file = formData[`${fieldKey}_file`];
    const questionKey = QUESTION_KEYS[fieldKey];
    const isQuestionMissing = questionKey ? missingQuestionKeys.includes(questionKey) : false;
    const uploadedForQuestion = questionKey ? uploadedAttachments[questionKey] : null;

    return (
      <div key={id} style={{ marginBottom: "20px", padding: "16px", background: isQuestionMissing ? "rgba(127,29,29,0.18)" : "rgba(30,41,59,0.5)", borderRadius: "8px", border: isQuestionMissing ? "1px solid rgba(248,113,113,0.8)" : "1px solid rgba(148,163,184,0.2)", boxShadow: isQuestionMissing ? "0 0 0 2px rgba(248,113,113,0.22)" : "none", opacity: isFormDisabled ? 0.6 : 1 }}>
        <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "12px", color: isQuestionMissing ? "#fca5a5" : "#e2e8f0" }}>
          {id} - {question}
          {isQuestionMissing && <span style={{ marginLeft: "8px", fontSize: "11px", color: "#fecaca" }}>• required</span>}
        </label>
        
        <div style={{ marginBottom: "12px" }}>
          {options ? (
            <select
              value={answer}
              onChange={(e) => {
                setFormData({ ...formData, [fieldKey]: e.target.value });
                clearQuestionValidation(questionKey);
              }}
              disabled={isFormDisabled}
              style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: isQuestionMissing ? "1px solid rgba(248,113,113,0.7)" : "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", cursor: isFormDisabled ? "not-allowed" : "pointer" }}
            >
              <option value="">-- Select --</option>
              {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          ) : (
            <input
              type="number"
              min="0"
              max="100"
              placeholder={showPercentage ? "e.g., 75" : ""}
              value={answer}
              onChange={(e) => {
                setFormData({ ...formData, [fieldKey]: e.target.value });
                clearQuestionValidation(questionKey);
              }}
              disabled={isFormDisabled}
              style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: isQuestionMissing ? "1px solid rgba(248,113,113,0.7)" : "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", cursor: isFormDisabled ? "not-allowed" : "default" }}
            />
          )}
          {showPercentage && <p style={{ fontSize: "11px", opacity: 0.6, margin: "4px 0 0 0" }}>Percentage (%)</p>}
        </div>

        {answer && answer !== "" && (
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontSize: "11px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>
              Description / Evidence
            </label>
            <textarea
              value={description}
              onChange={(e) => setFormData({ ...formData, [`${fieldKey}_description`]: e.target.value })}
              placeholder="Provide details..."
              disabled={isFormDisabled}
              style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", minHeight: "60px", fontFamily: "inherit", boxSizing: "border-box", resize: "vertical", cursor: isFormDisabled ? "not-allowed" : "text" }}
            />
          </div>
        )}

        <div style={{ marginBottom: "12px" }}>
          <label style={{ display: "block", fontSize: "11px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>
            Upload supporting documents
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/png"
            onChange={(e) => handleFileChange(fieldKey, e.target.files[0])}
            disabled={uploadStates[fieldKey]?.status === 'uploading' || isFormDisabled}
            style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", opacity: (uploadStates[fieldKey]?.status === 'uploading' || isFormDisabled) ? 0.5 : 1, cursor: isFormDisabled ? "not-allowed" : "pointer" }}
          />
          <p style={{ fontSize: "11px", opacity: 0.6, margin: "4px 0 0 0" }}>Max 20 MB • PDF, DOC, DOCX, XLS, XLSX, JPG, PNG</p>
          {getUploadStatusIndicator(fieldKey)}
          {uploadedForQuestion?.fileName && (
            <p style={{ fontSize: "11px", margin: "4px 0 0 0", color: "#86efac" }}>
              📎 Uploaded: {uploadedForQuestion.fileName}
            </p>
          )}
        </div>

      </div>
    );
  };

  return (
    <div className="company-root" style={{ minHeight: "100vh", background: 'url("/assets/sfondo-dashboard.jpg")', backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat", color: "#f5f5f5", fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", textShadow: "0 2px 6px rgba(0,0,0,0.7)" }}>
      {/* Overlay scuro quando una sezione è espansa */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.95)",
          opacity: expandedSection ? 1 : 0,
          pointerEvents: expandedSection ? "none" : "none",
          transition: "opacity 0.4s ease",
          zIndex: 0,
        }}
      />
      
      <header style={{ padding: "16px 24px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img
              src={LogoAppleTreeHD}
              alt="AppleTree Logo"
              onClick={() => navigate("/")}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.transition = "transform 0.15s ease";
                e.currentTarget.style.cursor = "pointer";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
              style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover" }}
            />
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>AppleTree</div>
              <div style={{ fontSize: 11, opacity: 1, textTransform: "uppercase", letterSpacing: 1, color: "#a5f3fc", textShadow: "0 2px 8px rgba(0,0,0,0.9), 0 0 12px rgba(165,243,252,0.4)", fontWeight: 600 }}>
                Company Verification
              </div>
            </div>
          </div>
          <button 
            onClick={() => navigate("/esg-dashboard")}
            aria-label="Back to dashboard"
            title="Back to dashboard"
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(30,41,59,0.9)";
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(15,23,42,0.7)";
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "none";
            }}
            style={{ width: "38px", height: "38px", display: "inline-flex", alignItems: "center", justifyContent: "center", background: "rgba(15,23,42,0.7)", color: "#f5f5f5", border: "1px solid rgba(148,163,184,0.4)", borderRadius: "50%", cursor: "pointer", fontSize: "16px", fontWeight: "700", backdropFilter: "blur(6px)", textShadow: "0 1px 3px rgba(0,0,0,0.7)", transition: "all 0.2s ease" }}
          >
            ←
          </button>
        </div>
        <WalletSection />
      </header>

      <main className="company-main" style={{ maxWidth: "900px", margin: "0 auto", padding: "32px 24px", position: "relative", zIndex: 1 }}>
        <div style={{ marginBottom: "32px" }}>
          <h1
            style={{
              fontSize: "30px",
              fontWeight: "800",
              marginBottom: "8px",
              background: "linear-gradient(135deg, #7CFFB2 0%, #22c55e 45%, #9d7bff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "0 6px 18px rgba(124,255,178,0.18)",
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.6))",
            }}
          >
            ESG Company Verification
          </h1>
          <p style={{ fontSize: "14px", opacity: 0.9, color: "#E6FFE8", textShadow: "0 2px 6px rgba(0,0,0,0.8)" }}>Complete all sections to submit your company for ESG certification</p>
        </div>

        {/* KYC Status and Payment Section - Two columns */}
        <div className="company-two-col" style={{ 
          display: "grid", 
          gridTemplateColumns: "1fr 1fr", 
          gap: "20px", 
          marginBottom: "20px" 
        }}>
          <KYCStatusSection address={address} isConnected={isConnected} />
          <CertificationFeeSection address={address} isConnected={isConnected} />
        </div>

        {/* Banner di blocco form: visibile quando pending o approved */}
        {isFormDisabled && (
          <div style={{
            marginBottom: "16px",
            padding: "14px 18px",
            background: submittedRequest?.status === 'approved'
              ? "linear-gradient(135deg, rgba(34,197,94,0.18), rgba(5,8,22,0.95))"
              : "linear-gradient(135deg, rgba(59,130,246,0.18), rgba(5,8,22,0.95))",
            border: submittedRequest?.status === 'approved'
              ? "1px solid rgba(34,197,94,0.5)"
              : "1px solid rgba(59,130,246,0.5)",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            flexWrap: "wrap",
            boxShadow: "0 4px 16px rgba(0,0,0,0.4)"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "20px" }}>
                {submittedRequest?.status === 'approved' ? '🔒' : '⏳'}
              </span>
              <div>
                <p style={{ margin: 0, fontSize: "13px", fontWeight: "700",
                  color: submittedRequest?.status === 'approved' ? "#7CFFB2" : "#93c5fd",
                  textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>
                  {submittedRequest?.status === 'approved'
                    ? "Form locked – Request already approved"
                    : "Form locked – Request under review"}
                </p>
                <p style={{ margin: "2px 0 0 0", fontSize: "11px", opacity: 0.75, color: "#cbd5e1" }}>
                  {submittedRequest?.status === 'approved'
                    ? "Your ESG certification has been approved. Click 'New Submission' to update your data."
                    : "An administrator is reviewing your request. You cannot edit the form while it is under review."}
                </p>
              </div>
            </div>
            {submittedRequest?.status === 'approved' && (
              <button
                onClick={() => {
                  cleanupESGData();
                  setSubmittedRequest(null);
                  setSubmitError("");
                  window.location.reload();
                }}
                onMouseEnter={(e) => { e.target.style.background = "linear-gradient(135deg, #15803d, #166534)"; e.target.style.transform = "scale(1.03)"; }}
                onMouseLeave={(e) => { e.target.style.background = "linear-gradient(135deg, #22c55e, #16a34a)"; e.target.style.transform = "scale(1)"; }}
                style={{
                  padding: "8px 16px",
                  background: "linear-gradient(135deg, #22c55e, #16a34a)",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "700",
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 8px rgba(34,197,94,0.4)",
                  transition: "all 0.2s ease"
                }}
              >
                ✨ New Submission
              </button>
            )}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {/* SECTION 1: COMPANY INFORMATION */}
          <div id="section-company" style={{ borderRadius: "12px", background: "linear-gradient(145deg, rgba(24,163,74,0.16), rgba(5,8,22,0.92))", border: "1px solid rgba(124,255,178,0.4)", boxShadow: "0 18px 35px rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", overflow: "hidden" }}>
            <button onClick={() => toggleSection("company")} style={{ width: "100%", padding: "16px", background: "rgba(30,41,59,0.5)", border: "none", color: "#E6FFE8", fontSize: "14px", fontWeight: "600", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: expandedSection === "company" ? "1px solid rgba(148,163,184,0.3)" : "none", textShadow: "0 1px 3px rgba(0,0,0,0.7)" }}>
              <span>📋 1. Company Information</span>
              <span>{expandedSection === "company" ? "−" : "+"}</span>
            </button>
            {expandedSection === "company" && (
              <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "14px", opacity: isFormDisabled ? 0.6 : 1 }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>Wallet Address</label>
                  <input type="text" value={formData.wallet} disabled style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.6)", color: "#7CFFB2", fontSize: "12px", cursor: "not-allowed", boxSizing: "border-box" }} />
                </div>
                <div><label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>First Name *</label><input type="text" placeholder="John" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} disabled={isFormDisabled} style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", cursor: isFormDisabled ? "not-allowed" : "text" }} /></div>
                <div><label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>Last Name *</label><input type="text" placeholder="Doe" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} disabled={isFormDisabled} style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", cursor: isFormDisabled ? "not-allowed" : "text" }} /></div>
                <div><label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>Email Address *</label><input type="email" placeholder="john@company.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} disabled={isFormDisabled} style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", cursor: isFormDisabled ? "not-allowed" : "text" }} /></div>
                <div><label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>Phone Number *</label><input type="tel" placeholder="+1 234 567 8900" value={formData.phoneNumber} onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })} disabled={isFormDisabled} style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", cursor: isFormDisabled ? "not-allowed" : "text" }} /></div>
                <div><label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>Company Name *</label><input type="text" placeholder="Your Company Ltd." value={formData.companyName} onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} disabled={isFormDisabled} style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", cursor: isFormDisabled ? "not-allowed" : "text" }} /></div>
                <div><label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>Registration Number</label><input type="text" placeholder="VAT/Tax ID" value={formData.registrationNumber} onChange={(e) => setFormData({ ...formData, registrationNumber: e.target.value })} disabled={isFormDisabled} style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", cursor: isFormDisabled ? "not-allowed" : "text" }} /></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>Country *</label>
                    <Select
                      options={countries.map((country) => ({
                        value: country.isoCode,
                        label: country.name,
                      }))}
                      value={
                        formData.addressCountry
                          ? {
                              value: formData.addressCountry,
                              label: countries.find((c) => c.isoCode === formData.addressCountry)?.name || "",
                            }
                          : null
                      }
                      onChange={(option) =>
                        setFormData({
                          ...formData,
                          addressCountry: option?.value || "",
                          addressState: "",
                          addressCity: "",
                        })
                      }
                      placeholder="-- Search country --"
                      isDisabled={isFormDisabled}
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>State/Region *</label>
                    <Select
                      options={states.map((state) => ({
                        value: state.isoCode,
                        label: state.name,
                      }))}
                      value={
                        formData.addressState && formData.addressCountry
                          ? {
                              value: formData.addressState,
                              label: states.find((s) => s.isoCode === formData.addressState)?.name || "",
                            }
                          : null
                      }
                      onChange={(option) =>
                        setFormData({
                          ...formData,
                          addressState: option?.value || "",
                          addressCity: "",
                        })
                      }
                      placeholder="-- Search state/region --"
                      isDisabled={isFormDisabled || !formData.addressCountry}
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                    />
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>City *</label>
                    <Select
                      options={cities.map((city) => ({
                        value: city.name,
                        label: city.name,
                      }))}
                      value={
                        formData.addressCity && formData.addressCountry
                          ? {
                              value: formData.addressCity,
                              label: formData.addressCity,
                            }
                          : null
                      }
                      onChange={(option) =>
                        setFormData({ ...formData, addressCity: option?.value || "" })
                      }
                      placeholder="-- Search city --"
                      isDisabled={isFormDisabled || !formData.addressCountry}
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>Address Line *</label>
                    <input
                      type="text"
                      placeholder="Street and number"
                      value={formData.addressLine}
                      onChange={(e) => setFormData({ ...formData, addressLine: e.target.value })}
                      disabled={isFormDisabled}
                      style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", cursor: isFormDisabled ? "not-allowed" : "text" }}
                    />
                  </div>
                </div>
                <div><label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>Position *</label><select value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} disabled={isFormDisabled} style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", cursor: isFormDisabled ? "not-allowed" : "pointer" }}><option value="">-- Select --</option><option value="ceo">CEO</option><option value="cfo">CFO</option><option value="cso">CSO</option><option value="manager">Manager</option><option value="other">Other</option></select></div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>Business Sector *</label>
                  <select value={formData.businessSector} onChange={(e) => setFormData({ ...formData, businessSector: e.target.value, businessSectorOther: e.target.value !== "other" ? "" : formData.businessSectorOther })} disabled={isFormDisabled} style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", cursor: isFormDisabled ? "not-allowed" : "pointer" }}>
                    <option value="">-- Select --</option>
                    <option value="tech">Technology</option>
                    <option value="finance">Finance</option>
                    <option value="energy">Energy</option>
                    <option value="agriculture">Agriculture</option>
                    <option value="retail">Retail</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="other">Other</option>
                  </select>
                  {formData.businessSector === "other" && (
                    <div style={{ marginTop: "8px" }}>
                      <label style={{ display: "block", fontSize: "11px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>Please specify *</label>
                      <input
                        type="text"
                        placeholder="Enter your business sector"
                        value={formData.businessSectorOther}
                        onChange={(e) => setFormData({ ...formData, businessSectorOther: e.target.value })}
                        disabled={isFormDisabled}
                        style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", cursor: isFormDisabled ? "not-allowed" : "text" }}
                      />
                    </div>
                  )}
                </div>
                <div><label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>Number of Employees *</label><select value={formData.numberOfEmployees} onChange={(e) => setFormData({ ...formData, numberOfEmployees: e.target.value })} disabled={isFormDisabled} style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", cursor: isFormDisabled ? "not-allowed" : "pointer" }}><option value="">-- Select --</option><option value="1-10">1-10</option><option value="11-50">11-50</option><option value="51-250">51-250</option><option value="251-1000">251-1000</option><option value="1000+">1000+</option></select></div>
                <div><label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>Company Website</label><input type="url" placeholder="https://www.company.com" value={formData.companyWebsite} onChange={(e) => setFormData({ ...formData, companyWebsite: e.target.value })} disabled={isFormDisabled} style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(15,23,42,0.9)", color: "#f5f5f5", fontSize: "12px", boxSizing: "border-box", cursor: isFormDisabled ? "not-allowed" : "text" }} /></div>
                
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "6px", opacity: 0.9 }}>Company Logo</label>
                  <p style={{ fontSize: "11px", opacity: 0.6, margin: "0 0 8px 0" }}>Upload your company logo (JPG, PNG - max 5MB - Optional)</p>
                  <FileUploader
                    questionKey="q11_company_logo"
                    label="Company Logo"
                    fileType="image"
                    description="Upload your company logo (JPG, PNG - max 5MB)"
                    onUploadComplete={(questionKey, attachment) => {
                      if (attachment) {
                        setUploadedAttachments(prev => ({ ...prev, [questionKey]: attachment }));
                        setFormData({ ...formData, companyLogo: attachment });
                      } else {
                        setUploadedAttachments(prev => {
                          const updated = { ...prev };
                          delete updated[questionKey];
                          return updated;
                        });
                        setFormData({ ...formData, companyLogo: null });
                      }
                    }}
                    onUploadError={(questionKey, error) => {
                      console.error(`Logo upload error:`, error);
                    }}
                    existingAttachment={uploadedAttachments['q11_company_logo']}
                  />
                </div>
              </div>
            )}
          </div>

          {/* SECTION 2: ENVIRONMENT */}
          <div id="section-environment" style={{ borderRadius: "12px", background: "linear-gradient(145deg, rgba(24,163,74,0.16), rgba(5,8,22,0.92))", border: "1px solid rgba(124,255,178,0.4)", boxShadow: "0 18px 35px rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", overflow: "hidden" }}>
            <button onClick={() => toggleSection("environment")} style={{ width: "100%", padding: "16px", background: "rgba(30,41,59,0.5)", border: "none", color: "#E6FFE8", fontSize: "14px", fontWeight: "600", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: expandedSection === "environment" ? "1px solid rgba(148,163,184,0.3)" : "none", textShadow: "0 1px 3px rgba(0,0,0,0.7)" }}>
              <span>🌱 2. E - ENVIRONMENT {getMissingCountForSection("E") > 0 ? `(Missing: ${getMissingCountForSection("E")})` : ""}</span>
              <span>{expandedSection === "environment" ? "−" : "+"}</span>
            </button>
            {expandedSection === "environment" && (
              <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "24px" }}>
                <div style={{ background: "rgba(124, 255, 178, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #7CFFB2" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#7CFFB2" }}>E1 - Emissions & Energy (30%)</h3>
                </div>
                {renderQuestion("E1.1", "Does the company measure at least annually its Scope 1 and 2 emissions?", "e1_1_ghgScope12", ["Yes", "No"])}
                {renderQuestion("E1.2", "Does the company structurally measure part or all of Scope 3 emissions?", "e1_2_scope3", ["Yes", "No"])}
                {renderQuestion("E1.3", "Are there clear reduction targets (e.g., % CO₂ reduction by year X)?", "e1_3_reductionTargets", ["Yes", "In Progress", "No"])}
                {renderQuestion("E1.4", "What percentage of energy comes from renewable sources?", "e1_4_renewableEnergy", null, true)}

                <div style={{ background: "rgba(124, 255, 178, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #7CFFB2" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#7CFFB2" }}>E2 - Resources & Waste (25%)</h3>
                </div>
                {renderQuestion("E2.1", "Is there a formalized waste management policy?", "e2_1_wastePolicy", ["Yes", "In Progress", "No"])}
                {renderQuestion("E2.2", "Does the company measure and improve its recycling/recovery rate?", "e2_2_recyclingRate", ["Yes", "No"])}
                {renderQuestion("E2.3", "Are there concrete initiatives to reduce packaging or critical materials?", "e2_3_packagingReduction", ["Yes", "In Progress", "No"])}

                <div style={{ background: "rgba(124, 255, 178, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #7CFFB2" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#7CFFB2" }}>E3 - Supply Chain & Environmental Impact (20%)</h3>
                </div>
                {renderQuestion("E3.1", "Are there minimum ESG criteria for selecting suppliers?", "e3_1_supplierCriteria", ["Yes", "In Progress", "No"])}
                {renderQuestion("E3.2", "Are high-impact suppliers periodically monitored on environmental issues?", "e3_2_supplierMonitoring", ["Yes", "In Progress", "No"])}

                <div style={{ background: "rgba(124, 255, 178, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #7CFFB2" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#7CFFB2" }}>E4 - Water & Pollution (15%)</h3>
                </div>
                {renderQuestion("E4.1", "Does the company measure and analyze water consumption?", "e4_1_waterMeasurement", ["Yes", "No"])}
                {renderQuestion("E4.2", "Are there procedures to manage wastewater/hydrocarbons/hazardous substances?", "e4_2_wastewater", ["Yes", "In Progress", "No"])}

                <div style={{ background: "rgba(124, 255, 178, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #7CFFB2" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#7CFFB2" }}>E5 - Green Innovation (10%)</h3>
                </div>
                {renderQuestion("E5.1", "Does the company offer products/services with clear environmental benefits?", "e5_1_greenProducts", ["Yes", "No"])}
                {renderQuestion("E5.2", "Does the company have environmental certifications?", "e5_2_certifications", ["Yes", "No"])}
              </div>
            )}
          </div>

          {/* SECTION 3: SOCIAL */}
          <div id="section-social" style={{ borderRadius: "12px", background: "linear-gradient(145deg, rgba(24,163,74,0.16), rgba(5,8,22,0.92))", border: "1px solid rgba(124,255,178,0.4)", boxShadow: "0 18px 35px rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", overflow: "hidden" }}>
            <button onClick={() => toggleSection("social")} style={{ width: "100%", padding: "16px", background: "rgba(30,41,59,0.5)", border: "none", color: "#E6FFE8", fontSize: "14px", fontWeight: "600", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: expandedSection === "social" ? "1px solid rgba(148,163,184,0.3)" : "none", textShadow: "0 1px 3px rgba(0,0,0,0.7)" }}>
              <span>👥 3. S - SOCIAL {getMissingCountForSection("S") > 0 ? `(Missing: ${getMissingCountForSection("S")})` : ""}</span>
              <span>{expandedSection === "social" ? "−" : "+"}</span>
            </button>
            {expandedSection === "social" && (
              <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "24px" }}>
                <div style={{ background: "rgba(59, 130, 246, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #3b82f6" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#3b82f6" }}>S1 - Workers & Working Conditions (30%)</h3>
                </div>
                {renderQuestion("S1.1", "Do almost all workers have regular contracts compliant with the law?", "s1_1_regularContracts", ["Yes", "Partial", "No"])}
                {renderQuestion("S1.2", "Is there a structured health and safety system (RSPP, DVR, training, PPE)?", "s1_2_healthSafety", ["Yes", "In Progress", "No"])}
                {renderQuestion("S1.3", "Does the company plan regular training (technical + soft + ESG)?", "s1_3_training", ["Yes", "No"])}

                <div style={{ background: "rgba(59, 130, 246, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #3b82f6" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#3b82f6" }}>S2 - Equity, Inclusion & Diversity (20%)</h3>
                </div>
                {renderQuestion("S2.1", "Is there an explicit anti-discrimination and harassment policy?", "s2_1_antiDiscrimination", ["Yes", "No"])}
                {renderQuestion("S2.2", "Does the company monitor and promote diversity in key roles?", "s2_2_diversity", ["Yes", "In Progress", "No"])}

                <div style={{ background: "rgba(59, 130, 246, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #3b82f6" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#3b82f6" }}>S3 - Human Rights & Supply Chain (20%)</h3>
                </div>
                {renderQuestion("S3.1", "Is there a written policy on child labor, forced labor, and human rights?", "s3_1_childLaborPolicy", ["Yes", "No"])}
                {renderQuestion("S3.2", "Does the company conduct checks on suppliers in high-risk areas?", "s3_2_supplyChainRights", ["Yes", "In Progress", "No"])}

                <div style={{ background: "rgba(59, 130, 246, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #3b82f6" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#3b82f6" }}>S4 - Community & Stakeholders (15%)</h3>
                </div>
                {renderQuestion("S4.1", "Does the company have active community initiatives?", "s4_1_communityRelations", ["Yes", "No"])}
                {renderQuestion("S4.2", "Are there structured listening channels (surveys, forums, committees)?", "s4_2_stakeholderEngagement", ["Yes", "No"])}

                <div style={{ background: "rgba(59, 130, 246, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #3b82f6" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#3b82f6" }}>S5 - Privacy, Data & Security (15%)</h3>
                </div>
                {renderQuestion("S5.1", "Are there GDPR policies and procedures with clear roles?", "s5_1_gdpr", ["Yes", "In Progress", "No"])}
                {renderQuestion("S5.2", "Does the company have minimum cybersecurity procedures?", "s5_2_cybersecurity", ["Yes", "In Progress", "No"])}
              </div>
            )}
          </div>

          {/* SECTION 4: GOVERNANCE */}
          <div id="section-governance" style={{ borderRadius: "12px", background: "linear-gradient(145deg, rgba(24,163,74,0.16), rgba(5,8,22,0.92))", border: "1px solid rgba(124,255,178,0.4)", boxShadow: "0 18px 35px rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", overflow: "hidden" }}>
            <button onClick={() => toggleSection("governance")} style={{ width: "100%", padding: "16px", background: "rgba(30,41,59,0.5)", border: "none", color: "#E6FFE8", fontSize: "14px", fontWeight: "600", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: expandedSection === "governance" ? "1px solid rgba(148,163,184,0.3)" : "none", textShadow: "0 1px 3px rgba(0,0,0,0.7)" }}>
              <span>⚖️ 4. G - GOVERNANCE {getMissingCountForSection("G") > 0 ? `(Missing: ${getMissingCountForSection("G")})` : ""}</span>
              <span>{expandedSection === "governance" ? "−" : "+"}</span>
            </button>
            {expandedSection === "governance" && (
              <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "24px" }}>
                <div style={{ background: "rgba(168, 85, 247, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #a855f7" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#a855f7" }}>G1 - Ethics & Transparency (30%)</h3>
                </div>
                {renderQuestion("G1.1", "Is there an ethics code approved and communicated to employees/suppliers?", "g1_1_ethicsCode", ["Yes", "In Progress", "No"])}
                {renderQuestion("G1.2", "Are there anti-corruption/AML procedures?", "g1_2_antiCorruption", ["Yes", "In Progress", "No"])}
                {renderQuestion("G1.3", "Does the company provide a whistleblowing channel?", "g1_3_whistleblowing", ["Yes", "In Progress", "No"])}

                <div style={{ background: "rgba(168, 85, 247, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #a855f7" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#a855f7" }}>G2 - Management Structure & Board (25%)</h3>
                </div>
                {renderQuestion("G2.1", "Is the shareholder/partner structure clear and documented?", "g2_1_ownershipStructure", ["Yes", "Partial", "No"])}
                {renderQuestion("G2.2", "Is there a governance body that meets with formal minutes?", "g2_2_governance", ["Yes", "No"])}
                {renderQuestion("G2.3", "Are there independent members/diversity on the board?", "g2_3_boardDiversity", ["Yes", "Partial", "No", "Not Applicable"])}

                <div style={{ background: "rgba(168, 85, 247, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #a855f7" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#a855f7" }}>G3 - Compliance & Regulation (20%)</h3>
                </div>
                {renderQuestion("G3.1", "Has the company identified main applicable regulations?", "g3_1_regulatoryMapping", ["Yes", "Partial", "No"])}
                {renderQuestion("G3.2", "Is there a process to stay updated on regulations?", "g3_2_regulatoryUpdates", ["Yes", "In Progress", "No"])}

                <div style={{ background: "rgba(168, 85, 247, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #a855f7" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#a855f7" }}>G4 - Reporting, Financial Statements & Audits (15%)</h3>
                </div>
                {renderQuestion("G4.1", "Are financial statements clear, filed, and if applicable, audited?", "g4_1_financialStatements", ["Yes", "Partial", "No"])}
                {renderQuestion("G4.2", "Does the company produce non-financial/ESG reports?", "g4_2_esgReporting", ["Yes", "In Progress", "No"])}

                <div style={{ background: "rgba(168, 85, 247, 0.05)", padding: "12px", borderRadius: "6px", borderLeft: "3px solid #a855f7" }}>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "700", color: "#a855f7" }}>G5 - Risks & Cybersecurity (10%)</h3>
                </div>
                {renderQuestion("G5.1", "Does the company have an assessment of main risks?", "g5_1_riskMapping", ["Yes", "Partial", "No"])}
                {renderQuestion("G5.2", "Is there a plan for critical incident response?", "g5_2_incidentResponse", ["Yes", "In Progress", "No"])}
              </div>
            )}
          </div>

          {/* SECTION 5: TRANSPARENCY & PRIVACY CONSENTS */}
          <div id="section-privacy" style={{ borderRadius: "12px", background: "linear-gradient(145deg, rgba(24,163,74,0.16), rgba(5,8,22,0.92))", border: "1px solid rgba(124,255,178,0.4)", boxShadow: "0 18px 35px rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", overflow: "hidden" }}>
            <button onClick={() => toggleSection("privacy")} style={{ width: "100%", padding: "16px", background: "rgba(30,41,59,0.5)", border: "none", color: "#E6FFE8", fontSize: "14px", fontWeight: "600", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: expandedSection === "privacy" ? "1px solid rgba(148,163,184,0.3)" : "none", textShadow: "0 1px 3px rgba(0,0,0,0.7)" }}>
              <span>🔒 5. Transparency & Privacy Consents *</span>
              <span>{expandedSection === "privacy" ? "−" : "+"}</span>
            </button>
            {expandedSection === "privacy" && (
              <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "20px", opacity: isFormDisabled ? 0.6 : 1 }}>
                {/* Important Notice */}
                <div style={{ background: "rgba(59,130,246,0.1)", border: "2px solid rgba(59,130,246,0.3)", borderRadius: "12px", padding: "16px", fontSize: "13px", lineHeight: "1.6", color: "#cbd5e1" }}>
                  <strong style={{ color: "#60a5fa", display: "block", marginBottom: "10px", fontSize: "14px" }}>
                    📋 Important Notice:
                  </strong>
                  <p style={{ margin: 0 }}>
                    By submitting, you confirm you have authority to act for the company and agree that AppleTree may process your data for ESG verification. 
                    If certified, a public certification report (JSON/PDF) may be published via IPFS and referenced on blockchain for transparency. 
                    You can choose map visibility (exact/city/country/none) below, and you may withdraw consent for visibility at any time, 
                    but content already published on IPFS/blockchain may persist due to the decentralized nature of these technologies.
                  </p>
                </div>

                {/* Privacy Policy & Terms Consent */}
                <div style={{ padding: "14px", background: "rgba(59,130,246,0.08)", borderRadius: "8px", border: "1px solid rgba(59,130,246,0.25)" }}>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: isFormDisabled ? "not-allowed" : "pointer", fontSize: "13px", lineHeight: "1.7" }}>
                    <input
                      type="checkbox"
                      checked={formData.privacyConsent}
                      onChange={(e) => setFormData({ ...formData, privacyConsent: e.target.checked })}
                      disabled={isFormDisabled}
                      style={{ marginTop: "3px", cursor: isFormDisabled ? "not-allowed" : "pointer", width: "16px", height: "16px", accentColor: "#7CFFB2", minWidth: "16px" }}
                    />
                    <span style={{ color: "#f5f5f5", fontWeight: "500" }}>
                      I confirm that I am authorized to act on behalf of the company and agree to the{" "}
                      <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: "#60a5fa", textDecoration: "underline" }}>
                        Privacy Policy
                      </Link>
                      {" "}and{" "}
                      <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: "#60a5fa", textDecoration: "underline" }}>
                        Terms of Service
                      </Link>
                      . I understand that, if certified, AppleTree may publish a public ESG certification report (including company name, website, ESG scores, and blockchain references) via IPFS and blockchain for transparency purposes.
                      <span style={{ color: "#ef4444", fontWeight: "700" }}> *</span>
                    </span>
                  </label>
                  {!formData.privacyConsent && (
                    <div style={{ marginTop: "8px", fontSize: "11px", color: "#fca5a5", fontStyle: "italic" }}>
                      ⚠️ This consent is required to proceed with your ESG certification request.
                    </div>
                  )}
                </div>

                {/* Map Visibility Choice */}
                <div style={{ padding: "14px", background: "rgba(168,85,247,0.08)", borderRadius: "8px", border: "1px solid rgba(168,85,247,0.25)" }}>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "700", marginBottom: "12px", color: "#E6FFE8" }}>
                    Public ESG Map visibility <span style={{ color: "#ef4444", fontWeight: "700" }}>*</span>
                  </label>
                  <p style={{ fontSize: "11px", opacity: 0.7, marginBottom: "14px", color: "#d1d5db" }}>
                    Choose how your company location will be displayed on the public AppleTree ESG Map:
                  </p>
                  
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {/* Option 1: EXACT_ADDRESS */}
                    <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "10px", background: formData.mapVisibility === "EXACT_ADDRESS" ? "rgba(124,255,178,0.12)" : "rgba(15,23,42,0.5)", borderRadius: "6px", border: formData.mapVisibility === "EXACT_ADDRESS" ? "1px solid rgba(124,255,178,0.4)" : "1px solid rgba(148,163,184,0.2)", cursor: isFormDisabled ? "not-allowed" : "pointer", transition: "all 0.2s ease" }}>
                      <input
                        type="radio"
                        name="mapVisibility"
                        value="EXACT_ADDRESS"
                        checked={formData.mapVisibility === "EXACT_ADDRESS"}
                        onChange={(e) => setFormData({ ...formData, mapVisibility: e.target.value })}
                        disabled={isFormDisabled}
                        style={{ marginTop: "2px", cursor: isFormDisabled ? "not-allowed" : "pointer", accentColor: "#7CFFB2" }}
                      />
                      <span style={{ fontSize: "12px", color: "#f5f5f5", lineHeight: "1.5" }}>
                        ✅ Yes – show our exact business address on the public AppleTree ESG Map
                      </span>
                    </label>

                    {/* Option 2: CITY_REGION (Default) */}
                    <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "10px", background: formData.mapVisibility === "CITY_REGION" ? "rgba(124,255,178,0.12)" : "rgba(15,23,42,0.5)", borderRadius: "6px", border: formData.mapVisibility === "CITY_REGION" ? "1px solid rgba(124,255,178,0.4)" : "1px solid rgba(148,163,184,0.2)", cursor: isFormDisabled ? "not-allowed" : "pointer", transition: "all 0.2s ease" }}>
                      <input
                        type="radio"
                        name="mapVisibility"
                        value="CITY_REGION"
                        checked={formData.mapVisibility === "CITY_REGION"}
                        onChange={(e) => setFormData({ ...formData, mapVisibility: e.target.value })}
                        disabled={isFormDisabled}
                        style={{ marginTop: "2px", cursor: isFormDisabled ? "not-allowed" : "pointer", accentColor: "#7CFFB2" }}
                      />
                      <span style={{ fontSize: "12px", color: "#f5f5f5", lineHeight: "1.5" }}>
                        🟡 Yes – show only city/region (approximate location) on the public AppleTree ESG Map
                      </span>
                    </label>

                    {/* Option 3: COUNTRY_ONLY */}
                    <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "10px", background: formData.mapVisibility === "COUNTRY_ONLY" ? "rgba(124,255,178,0.12)" : "rgba(15,23,42,0.5)", borderRadius: "6px", border: formData.mapVisibility === "COUNTRY_ONLY" ? "1px solid rgba(124,255,178,0.4)" : "1px solid rgba(148,163,184,0.2)", cursor: isFormDisabled ? "not-allowed" : "pointer", transition: "all 0.2s ease" }}>
                      <input
                        type="radio"
                        name="mapVisibility"
                        value="COUNTRY_ONLY"
                        checked={formData.mapVisibility === "COUNTRY_ONLY"}
                        onChange={(e) => setFormData({ ...formData, mapVisibility: e.target.value })}
                        disabled={isFormDisabled}
                        style={{ marginTop: "2px", cursor: isFormDisabled ? "not-allowed" : "pointer", accentColor: "#7CFFB2" }}
                      />
                      <span style={{ fontSize: "12px", color: "#f5f5f5", lineHeight: "1.5" }}>
                        ❌ Yes – show only country (approximate location) on the public AppleTree ESG Map
                      </span>
                    </label>

                    {/* Option 4: HIDDEN */}
                    <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "10px", background: formData.mapVisibility === "HIDDEN" ? "rgba(124,255,178,0.12)" : "rgba(15,23,42,0.5)", borderRadius: "6px", border: formData.mapVisibility === "HIDDEN" ? "1px solid rgba(124,255,178,0.4)" : "1px solid rgba(148,163,184,0.2)", cursor: isFormDisabled ? "not-allowed" : "pointer", transition: "all 0.2s ease" }}>
                      <input
                        type="radio"
                        name="mapVisibility"
                        value="HIDDEN"
                        checked={formData.mapVisibility === "HIDDEN"}
                        onChange={(e) => setFormData({ ...formData, mapVisibility: e.target.value })}
                        disabled={isFormDisabled}
                        style={{ marginTop: "2px", cursor: isFormDisabled ? "not-allowed" : "pointer", accentColor: "#7CFFB2" }}
                      />
                      <span style={{ fontSize: "12px", color: "#f5f5f5", lineHeight: "1.5" }}>
                        🚫 No – do not show any location on the public map
                      </span>
                    </label>
                  </div>

                  <p style={{ fontSize: "11px", opacity: 0.6, marginTop: "12px", color: "#93c5fd", fontStyle: "italic" }}>
                    💡 You can change or withdraw this consent anytime.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Messaggio di stato richiesta - Sempre visibile dopo il submit */}
        {submittedRequest && (
          <div style={{ 
            marginTop: "24px", 
            padding: "16px", 
            background: submittedRequest.status === 'approved' 
              ? "linear-gradient(145deg, rgba(34,197,94,0.16), rgba(5,8,22,0.92))" 
              : submittedRequest.status === 'rejected' 
                ? "linear-gradient(145deg, rgba(239,68,68,0.16), rgba(5,8,22,0.92))"
                : "linear-gradient(145deg, rgba(59,130,246,0.16), rgba(5,8,22,0.92))", 
            border: submittedRequest.status === 'approved' 
              ? "1px solid rgba(34,197,94,0.4)" 
              : submittedRequest.status === 'rejected'
                ? "1px solid rgba(239,68,68,0.4)"
                : "1px solid rgba(59,130,246,0.4)", 
            borderRadius: "12px", 
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)"
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "20px" }}>
                  {submittedRequest.status === 'approved' ? '✅' : 
                   submittedRequest.status === 'rejected' ? '❌' : 
                   submittedRequest.status === 'need_info' ? 'ℹ️' : '⏳'}
                </span>
                <h3 style={{ 
                  margin: 0, 
                  fontSize: "16px", 
                  fontWeight: "700",
                  color: submittedRequest.status === 'approved' 
                    ? "#22c55e" 
                    : submittedRequest.status === 'rejected'
                      ? "#ef4444"
                      : submittedRequest.status === 'need_info'
                        ? "#f59e0b"
                        : "#3b82f6"
                }}>
                  {submittedRequest.status === 'approved' 
                    ? 'Request Approved!' 
                    : submittedRequest.status === 'rejected'
                      ? 'Request Rejected'
                      : submittedRequest.status === 'need_info'
                        ? 'Additional Information Required'
                        : 'Request Submitted Successfully'}
                </h3>
              </div>
              <div style={{ 
                padding: "4px 12px", 
                background: submittedRequest.status === 'approved' 
                  ? "rgba(34,197,94,0.2)" 
                  : submittedRequest.status === 'rejected'
                    ? "rgba(239,68,68,0.2)"
                    : submittedRequest.status === 'need_info'
                      ? "rgba(245,158,11,0.2)"
                      : "rgba(59,130,246,0.2)", 
                borderRadius: "6px",
                fontSize: "11px",
                fontWeight: "600",
                textTransform: "uppercase",
                color: submittedRequest.status === 'approved' 
                  ? "#7CFFB2" 
                  : submittedRequest.status === 'rejected'
                    ? "#fca5a5"
                    : submittedRequest.status === 'need_info'
                      ? "#fbbf24"
                      : "#93c5fd"
              }}>
                {submittedRequest.status === 'approved' ? 'Approved' : 
                 submittedRequest.status === 'rejected' ? 'Rejected' : 
                 submittedRequest.status === 'need_info' ? 'Need Info' : 'Pending'}
              </div>
            </div>
            
            <div style={{ fontSize: "13px", color: "#f5f5f5", lineHeight: "1.6" }}>
              <p style={{ margin: "0 0 8px 0" }}>
                {submittedRequest.status === 'pending' && (
                  <>Your ESG verification request has been submitted successfully. An administrator will review it shortly.</>
                )}
                {submittedRequest.status === 'approved' && (
                  <>Your ESG verification request has been approved! You can proceed with the next steps.</>
                )}
                {submittedRequest.status === 'rejected' && (
                  <>Your ESG verification request has been rejected. Please review the reasons and resubmit the questionnaire.</>
                )}
                {submittedRequest.status === 'need_info' && (
                  <>The administrator requires additional information. Please update the questionnaire with the requested details.</>
                )}
              </p>
              
              <div style={{ 
                marginTop: "12px", 
                padding: "12px", 
                background: "rgba(0,0,0,0.3)", 
                borderRadius: "6px",
                fontFamily: "monospace",
                fontSize: "12px"
              }}>
                <div style={{ marginBottom: "6px" }}>
                  <span style={{ opacity: 0.7 }}>Request ID:</span>{' '}
                  <span style={{ color: "#7CFFB2", fontWeight: "600" }}>{submittedRequest.requestId}</span>
                </div>
                <div style={{ marginBottom: "6px" }}>
                  <span style={{ opacity: 0.7 }}>Company:</span>{' '}
                  <span style={{ color: "#f5f5f5" }}>{submittedRequest.companyName}</span>
                </div>
                <div style={{ marginBottom: "6px" }}>
                  <span style={{ opacity: 0.7 }}>Email:</span>{' '}
                  <span style={{ color: "#f5f5f5" }}>{submittedRequest.email}</span>
                </div>
                <div>
                  <span style={{ opacity: 0.7 }}>Submitted:</span>{' '}
                  <span style={{ color: "#f5f5f5" }}>
                    {formatEnglishDate(submittedRequest.createdAt)}
                  </span>
                </div>
              </div>
              
              {(submittedRequest.status === 'rejected' || submittedRequest.status === 'need_info' || submittedRequest.status === 'approved') && (
                <button
                  onClick={() => {
                    // Pulisci tutti i dati ESG salvati
                    cleanupESGData();
                    setSubmittedRequest(null);
                    setSubmitError("");
                    // Ricarica la pagina per resettare il form
                    window.location.reload();
                  }}
                  style={{
                    marginTop: "12px",
                    padding: "10px 20px",
                    background: submittedRequest.status === 'approved'
                      ? "linear-gradient(135deg, #22c55e, #16a34a)"
                      : "linear-gradient(135deg, #f59e0b, #d97706)",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = submittedRequest.status === 'approved'
                      ? "linear-gradient(135deg, #15803d, #166534)"
                      : "linear-gradient(135deg, #d97706, #b45309)";
                    e.target.style.transform = "scale(1.02)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = submittedRequest.status === 'approved'
                      ? "linear-gradient(135deg, #22c55e, #16a34a)"
                      : "linear-gradient(135deg, #f59e0b, #d97706)";
                    e.target.style.transform = "scale(1)";
                  }}
                >
                  {submittedRequest.status === 'approved' ? '✨ New Submission' : '🔄 Resubmit Questionnaire'}
                </button>
              )}
              
              <div style={{ 
                marginTop: "12px", 
                padding: "8px 12px", 
                background: "rgba(59,130,246,0.1)", 
                borderRadius: "6px",
                fontSize: "11px",
                color: "#93c5fd"
              }}>
                ℹ️ The request status will update automatically when an administrator processes it.
              </div>
            </div>
          </div>
        )}

        {/* Submit Buttons */}
        <div style={{ marginTop: "32px", display: "flex", gap: "12px", justifyContent: "center" }}>
          <button 
            onClick={() => navigate("/esg-dashboard")}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(30,41,59,0.9)";
              e.target.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(15,23,42,0.7)";
              e.target.style.transform = "scale(1)";
            }}
            style={{ padding: "12px 24px", background: "rgba(15,23,42,0.7)", color: "#f5f5f5", border: "1px solid rgba(148,163,184,0.4)", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "600", backdropFilter: "blur(6px)", textShadow: "0 1px 2px rgba(0,0,0,0.7)", transition: "all 0.2s ease" }}
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            disabled={submitting || isFormDisabled || !formData.privacyConsent}
            onMouseEnter={(e) => {
              if (!submitting && !isFormDisabled && formData.privacyConsent) {
                e.target.style.transform = "scale(1.05)";
                e.target.style.boxShadow = "0 12px 24px rgba(34,197,94,0.6)";
              }
            }}
            onMouseLeave={(e) => {
              if (!submitting && !isFormDisabled && formData.privacyConsent) {
                e.target.style.transform = "scale(1)";
                e.target.style.boxShadow = "0 8px 18px rgba(34,197,94,0.4)";
              }
            }}
            style={{ padding: "12px 24px", background: (submitting || isFormDisabled || !formData.privacyConsent) ? "#6b7280" : "linear-gradient(135deg, #7CFFB2, #22C55E)", color: "#020617", border: "none", borderRadius: "8px", cursor: (submitting || isFormDisabled || !formData.privacyConsent) ? "not-allowed" : "pointer", fontSize: "14px", fontWeight: "700", textShadow: "0 1px 2px rgba(0,0,0,0.2)", boxShadow: "0 8px 18px rgba(34,197,94,0.4)", opacity: (submitting || isFormDisabled || !formData.privacyConsent) ? 0.7 : 1, transition: "all 0.2s ease" }}
          >
            {submitting ? "Submitting..." : isFormDisabled ? "Already Submitted" : "Submit Verification"}
          </button>
        </div>

        {/* Validation Error for Privacy Consent */}
        {!formData.privacyConsent && !isFormDisabled && (
          <div style={{ 
            marginTop: "12px", 
            padding: "12px 16px", 
            background: "rgba(239, 68, 68, 0.35)", 
            border: "1.5px solid rgba(239, 68, 68, 0.6)", 
            borderRadius: "8px", 
            color: "#fff", 
            fontSize: "13px", 
            textAlign: "center", 
            backdropFilter: "blur(12px)", 
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.5)",
            textShadow: "0 1px 3px rgba(0,0,0,0.9)",
            fontWeight: "600"
          }}>
            ⚠️ Please accept the Privacy Policy and Terms to submit your ESG verification request.
          </div>
        )}

        {submitError && (
          <div style={{ marginTop: "16px", padding: "12px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "8px", color: "#fca5a5", fontSize: "13px" }}>
            ✗ {submitError}
          </div>
        )}

        {/* Info Box */}
        <div style={{ marginTop: "24px", padding: "16px", borderRadius: "12px", background: "linear-gradient(145deg, rgba(24,163,74,0.16), rgba(5,8,22,0.85))", border: "1px solid rgba(124,255,178,0.35)", boxShadow: "0 8px 32px rgba(0,0,0,0.3)", backdropFilter: "blur(6px)", fontSize: "12px", opacity: 0.95, color: "#f8fafc", textShadow: "0 1px 3px rgba(0,0,0,0.7)" }}>
          <p style={{ margin: 0 }}>
            ℹ️ <strong>Note:</strong> All documents must be in English or provide certified translations. Maximum file size: 20MB per document.
          </p>
        </div>
      </main>
      <LegalFooter />
    </div>
  );
};

export default CompanyVerification;
